import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { MoreVertical } from 'lucide-react';

export type DropdownMenuItem =
  | { divider: true }
  | {
      divider?: false;
      label: string;
      /** Leading icon, drawn before the label. */
      icon?: React.ReactNode;
      /** Stable selector, independent of the label; rendered as `<menu testId>-item-<testId>`. */
      testId: string;
      onClick?: () => void;
      /** Renders a real link (`role="menuitem"`) so middle-click and copy-link work; still closes the menu. */
      href?: string;
      destructive?: boolean;
      disabled?: boolean;
      /** Why the item is disabled; shown as its tooltip so a disabled action explains itself. */
      disabledReason?: string;
    };

export interface DropdownMenuProps {
  items: DropdownMenuItem[];
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  testId?: string;
  /** Names the ⋮ trigger (aria-label and title), e.g. `Actions for Welcome message`. */
  ariaLabel?: string;
  /** Replaces the ⋮ icon button, e.g. an "add ▾" chip. `label` is its accessible name. */
  trigger?: {
    label: string;
    content: React.ReactNode;
    className?: string;
  };
}

export function DropdownMenu({
  items,
  isOpen: controlledIsOpen,
  onOpenChange,
  testId,
  ariaLabel,
  trigger,
}: DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    openUpward: boolean;
    ready: boolean;
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Sync with controlled prop if provided
  useEffect(() => {
    if (controlledIsOpen !== undefined) {
      setIsOpen(controlledIsOpen);
    }
  }, [controlledIsOpen]);

  const toggleOpen = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    onOpenChange?.(newState);
  };

  const closeMenu = (returnFocus = false) => {
    setIsOpen(false);
    onOpenChange?.(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  const enabledItems = () =>
    Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? [],
    );

  // Focus moves into the menu once it is positioned (never while it is still invisible).
  useEffect(() => {
    if (position?.ready) enabledItems()[0]?.focus();
  }, [position?.ready]);

  function handleMenuKeyDown(event: React.KeyboardEvent) {
    const items = enabledItems();
    const current = items.indexOf(document.activeElement as HTMLElement);
    const move = (index: number) => {
      event.preventDefault();
      items[(index + items.length) % items.length]?.focus();
    };
    if (event.key === 'ArrowDown') move(current + 1);
    else if (event.key === 'ArrowUp') move(current < 0 ? -1 : current - 1);
    else if (event.key === 'Home') move(0);
    else if (event.key === 'End') move(-1);
    else if (event.key === 'Tab') closeMenu(true);
  }

  // Mount the menu below the trigger first (invisible), then measure its
  // real rendered height and settle on a final position: below when it
  // fits, flipped above when it doesn't, clamped to the viewport when it's
  // taller than both. Replaces the old fixed-height estimate.
  useEffect(() => {
    if (!isOpen || !triggerRef.current) {
      setPosition(null);
      return;
    }

    const rect = triggerRef.current.getBoundingClientRect();
    setPosition({ top: rect.bottom, left: rect.right, openUpward: false, ready: false });
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || !position || position.ready || !triggerRef.current || !menuRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current.getBoundingClientRect().height;
    const fitsBelow = triggerRect.bottom + menuHeight <= window.innerHeight;
    const fitsAbove = triggerRect.top - menuHeight >= 0;
    const openUpward = !fitsBelow && fitsAbove;

    let top: number;
    if (openUpward) {
      top = triggerRect.top;
    } else if (fitsBelow) {
      top = triggerRect.bottom;
    } else {
      // Taller than the viewport either way — clamp so it never runs past
      // the top or bottom edge.
      top = Math.max(0, window.innerHeight - menuHeight);
    }

    // The menu is right-aligned to the trigger (-translate-x-full), so its left
    // edge is `left - menuWidth`. A trigger within a menu-width of the left edge
    // (a narrow viewport, a scrolled table) would push it off screen; clamp so it
    // stays fully inside on both sides.
    const menuWidth = menuRef.current.getBoundingClientRect().width;
    const left = Math.min(Math.max(triggerRect.right, menuWidth), window.innerWidth);

    setPosition({ top, left, openUpward, ready: true });
  }, [isOpen, position]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      closeMenu();
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        closeMenu(true);
      }
    }

    function handleScroll() {
      closeMenu();
    }

    function handleResize() {
      closeMenu();
    }

    document.addEventListener('keydown', handleKeyDown);

    // Defer attaching scroll/resize close listeners until the frame after
    // open: the first open of the session can trigger a synchronous
    // scroll/resize burst (portal mount shifting layout, scrollbar
    // appearing) that would otherwise close the menu before it ever renders.
    const frame = requestAnimationFrame(() => {
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
    });

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleOpen}
        aria-label={trigger?.label ?? ariaLabel ?? 'Open actions menu'}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        title={trigger ? undefined : (ariaLabel ?? 'Open actions menu')}
        data-testid={testId ? `${testId}-trigger` : undefined}
        className={
          trigger
            ? `focus-ring ${trigger.className ?? ''}`
            : 'focus-ring p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-600 dark:text-gray-400'
        }
      >
        {trigger ? trigger.content : <MoreVertical className="size-4" aria-hidden="true" />}
      </button>

      {isOpen &&
        position &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            onKeyDown={handleMenuKeyDown}
            data-testid={testId ? `${testId}-menu` : undefined}
            // The one permitted inline style: computed position, as custom properties read by the classes below.
            style={
              {
                '--menu-top': `${position.top}px`,
                '--menu-left': `${position.left}px`,
              } as React.CSSProperties
            }
            className={`fixed top-(--menu-top) left-(--menu-left) -translate-x-full ${position.ready ? 'visible' : 'invisible'} w-48 rounded-lg bg-white dark:bg-gray-900 shadow-lg border border-gray-200 dark:border-gray-800 py-1 z-60 ${position.openUpward ? '-translate-y-full -mt-1' : 'mt-1'}`}
          >
            {items.map((item, index) => {
              if (item.divider) {
                return (
                  <div
                    key={index}
                    role="separator"
                    className="my-1 border-t border-gray-200 dark:border-gray-800"
                  />
                );
              }

              const testIdAttr = testId ? `${testId}-item-${item.testId}` : undefined;
              const className = `flex w-full items-center gap-2 text-left px-4 py-2 text-sm transition-colors focus-visible:outline-none ${
                item.disabled
                  ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
                  : item.destructive
                    ? 'text-error-600 dark:text-error-400 hover:bg-error-50 focus-visible:bg-error-50 dark:hover:bg-error-500/10 dark:focus-visible:bg-error-500/10'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 focus-visible:bg-gray-50 dark:hover:bg-gray-700 dark:focus-visible:bg-gray-700'
              }`;

              if (item.href && !item.disabled) {
                return (
                  <Link
                    key={index}
                    to={item.href}
                    role="menuitem"
                    onClick={() => {
                      item.onClick?.();
                      closeMenu(true);
                    }}
                    data-testid={testIdAttr}
                    className={className}
                  >
                    {item.icon}
                    {item.label}
                  </Link>
                );
              }

              return (
                <button
                  key={index}
                  role="menuitem"
                  onClick={() => {
                    if (item.disabled) return;
                    item.onClick?.();
                    closeMenu(true);
                  }}
                  disabled={item.disabled}
                  title={item.disabled ? item.disabledReason : undefined}
                  data-testid={testIdAttr}
                  className={className}
                >
                  {item.icon}
                  {item.label}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}
